import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import ReactDOM from "react-dom";
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  startAfter,
} from "firebase/firestore";
import { firestoreProd, firestoreDev } from "../../firebase";
import {
  FaBuilding,
  FaSearch,
  FaDownload,
  FaSync,
  FaCommentDots,
  FaComments,
  FaUsers,
  FaClock,
  FaRegEnvelope,
  FaInfoCircle,
  FaChevronDown,
  FaChevronRight,
  FaFileCode,
  FaExternalLinkAlt,
  FaCopy,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  attachLoanMeta,
  chunk,
  createLoanCache,
  loanAppUrl,
  orgDisplayName,
  resolveAuthors,
  resolveLoansForPairs,
} from "./chatContext";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const tsToMs = (v) => {
  if (!v) return 0;
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = Date.parse(v);
    return isNaN(n) ? 0 : n;
  }
  if (v.seconds) return v.seconds * 1000;
  if (v._seconds) return v._seconds * 1000;
  if (typeof v.toDate === "function") return v.toDate().getTime();
  return 0;
};

const fmtDate = (ms) => {
  if (!ms) return "—";
  try {
    return new Date(ms).toISOString().slice(0, 10);
  } catch {
    return "—";
  }
};

const fmtDateTime = (ms) => {
  if (!ms) return "—";
  try {
    const d = new Date(ms);
    return d.toLocaleString();
  } catch {
    return "—";
  }
};

async function fetchUsersByEmail(db, emails) {
  const byEmail = new Map();
  const unique = [
    ...new Set(emails.filter(Boolean).map((e) => e.toLowerCase())),
  ];
  for (const group of chunk(unique, 10)) {
    const q = query(collection(db, "users"), where("email", "in", group));
    const snap = await getDocs(q);
    snap.forEach((d) => {
      const data = d.data();
      byEmail.set((data.email || "").toLowerCase(), { id: d.id, ...data });
    });
  }
  return byEmail;
}

async function fetchChatsByUids(db, uids) {
  const all = [];
  const unique = [...new Set(uids.filter(Boolean))];
  for (const group of chunk(unique, 10)) {
    const q = query(
      collection(db, "sidebar-chat-history"),
      where("uid", "in", group)
    );
    const snap = await getDocs(q);
    snap.forEach((d) => {
      all.push({ _docId: d.id, ...d.data() });
    });
  }
  return all;
}

const SUGGESTION_LIMIT = 12;

async function loadAllOrgs(db) {
  const snap = await getDocs(query(collection(db, "organizations")));
  return snap.docs
    .map((d) => {
      const data = d.data();
      return {
        id: d.id,
        name: data.name || data.organizationName || data.companyName || "",
        defaultForUserId:
          data.defaultForUserId ||
          data.adminUserId ||
          data.creatorUserId ||
          "",
        plan: data.subscription?.priceId || "",
      };
    })
    .sort((a, b) => orgDisplayName(a).localeCompare(orgDisplayName(b)));
}

function getInitials(name) {
  const out = [];
  for (let i = 0; i < name.length; i++) {
    const c = name[i];
    if (!/[A-Za-z0-9]/.test(c)) continue;
    const prev = i > 0 ? name[i - 1] : "";
    const next = i < name.length - 1 ? name[i + 1] : "";
    const isWordStart = i === 0 || !/[A-Za-z0-9]/.test(prev);
    const isCamelBoundary = /[a-z0-9]/.test(prev) && /[A-Z]/.test(c);
    const isAcronymChar =
      /[A-Z]/.test(c) && (/[A-Z]/.test(prev) || /[A-Z]/.test(next));
    if (isWordStart || isCamelBoundary || isAcronymChar) {
      out.push(c.toLowerCase());
    }
  }
  return out.join("");
}

function fuzzyMatchOrgs(orgs, term) {
  const raw = (term || "").trim().toLowerCase();
  if (!raw) return [];
  const normTerm = raw.replace(/[^a-z0-9]/g, "");
  if (!normTerm) return [];

  const tightSubsequence = (name) => {
    if (normTerm.length < 4) return false;
    const normName = name.toLowerCase().replace(/[^a-z0-9]/g, "");
    let i = 0;
    let firstIdx = -1;
    let lastIdx = -1;
    for (let j = 0; j < normName.length && i < normTerm.length; j++) {
      if (normName[j] === normTerm[i]) {
        if (firstIdx === -1) firstIdx = j;
        lastIdx = j;
        i++;
      }
    }
    if (i !== normTerm.length) return false;
    const span = lastIdx - firstIdx + 1;
    return span <= normTerm.length * 4;
  };

  const tiered = [];
  for (const o of orgs) {
    const name = orgDisplayName(o);
    if (!name) continue;
    const lower = name.toLowerCase();
    const initials = getInitials(name);

    let tier = 0;
    if (lower === raw) tier = 5;
    else if (lower.startsWith(raw)) tier = 4;
    else if (initials.startsWith(normTerm)) tier = 3;
    else if (lower.includes(raw)) tier = 2;
    else if (initials.includes(normTerm)) tier = 2;
    else if (tightSubsequence(name)) tier = 1;

    if (tier > 0) tiered.push({ org: o, tier, name });
  }

  tiered.sort((a, b) => {
    if (b.tier !== a.tier) return b.tier - a.tier;
    return a.name.localeCompare(b.name);
  });

  return tiered.slice(0, SUGGESTION_LIMIT).map((t) => t.org);
}

async function findOrgIdsByEmail(db, email) {
  const q = query(
    collection(db, "organization-members"),
    where("email", "==", email.toLowerCase())
  );
  const snap = await getDocs(q);
  const ids = new Set();
  snap.forEach((d) => {
    const oid = d.data().organizationId;
    if (oid) ids.add(oid);
  });
  return [...ids];
}

// Aggregate chat docs into one row per user (uid).
function aggregateChatsByUser(chats, membersByUid) {
  const byUid = new Map();

  // Seed with every member so "silent" users still appear.
  for (const [uid, m] of membersByUid.entries()) {
    byUid.set(uid, {
      uid,
      email: m.email,
      role: m.role,
      status: m.status,
      chatCount: 0,
      messageCount: 0,
      firstChatAt: 0,
      lastActiveAt: 0,
      loanIds: new Set(),
      chats: [],
    });
  }

  for (const c of chats) {
    const uid = c.uid;
    if (!uid) continue;
    let row = byUid.get(uid);
    if (!row) {
      // Chat uid isn't a known org member — skip; shouldn't happen since we
      // query by member uids, but guard anyway.
      continue;
    }
    const msgCount = Array.isArray(c.messages) ? c.messages.length : 0;
    const started = tsToMs(c.dateStarted);
    const active = tsToMs(c.lastActive) || started;

    row.chatCount += 1;
    row.messageCount += msgCount;
    row.firstChatAt =
      row.firstChatAt === 0 ? started : Math.min(row.firstChatAt, started);
    row.lastActiveAt = Math.max(row.lastActiveAt, active);
    if (c.loanId) row.loanIds.add(c.loanId);
    row.chats.push({
      chatId: c.chatId || c._docId,
      chatName: c.chatName || "Chat",
      dateStarted: started,
      lastActive: active,
      messageCount: msgCount,
      loanId: c.loanId || null,
      clientHostname: c.clientHostname || "",
      messages: Array.isArray(c.messages) ? c.messages : [],
      raw: c,
    });
  }

  const rows = [...byUid.values()].map((r) => ({
    ...r,
    loanIds: [...r.loanIds],
    chats: r.chats.sort((a, b) => b.lastActive - a.lastActive),
  }));

  // Sort: active users first, by last activity desc, then silent users by email.
  rows.sort((a, b) => {
    if (a.chatCount === 0 && b.chatCount === 0) {
      return (a.email || "").localeCompare(b.email || "");
    }
    if (a.chatCount === 0) return 1;
    if (b.chatCount === 0) return -1;
    return b.lastActiveAt - a.lastActiveAt;
  });

  return rows;
}

async function runReport(db, orgId) {
  const membersQ = query(
    collection(db, "organization-members"),
    where("organizationId", "==", orgId)
  );
  const membersSnap = await getDocs(membersQ);
  const members = membersSnap.docs.map((d) => ({ _docId: d.id, ...d.data() }));
  if (members.length === 0) return { rows: [], chatCount: 0 };

  const emails = members.map((m) => m.email).filter(Boolean);
  const usersByEmail = await fetchUsersByEmail(db, emails);

  // Build members-by-uid map (only members who have a user doc).
  const membersByUid = new Map();
  for (const m of members) {
    const email = (m.email || "").toLowerCase();
    const user = usersByEmail.get(email);
    if (!user || !user.id) continue;
    membersByUid.set(user.id, {
      email: m.email || "",
      role: m.role || "",
      status: m.status || "",
    });
  }

  const uids = [...membersByUid.keys()];
  const chats = uids.length === 0 ? [] : await fetchChatsByUids(db, uids);

  const rows = aggregateChatsByUser(chats, membersByUid);
  return { rows, chatCount: chats.length };
}

function downloadCsv(filename, rows) {
  const headers = [
    "email",
    "role",
    "status",
    "chatCount",
    "messageCount",
    "firstChatAt",
    "lastActiveAt",
    "distinctLoanIds",
    "loanIds",
  ];
  const esc = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
  const body = rows
    .map((r) =>
      headers
        .map((h) => {
          if (h === "firstChatAt" || h === "lastActiveAt")
            return esc(r[h] ? new Date(r[h]).toISOString() : "");
          if (h === "distinctLoanIds") return esc(r.loanIds.length);
          if (h === "loanIds") return esc((r.loanIds || []).join("|"));
          return esc(r[h]);
        })
        .join(",")
    )
    .join("\n");
  const csv = headers.join(",") + "\n" + body;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const QA_CSV_COLUMNS = [
  ["askedAt", (p) => (p.ts ? new Date(p.ts).toISOString() : "")],
  ["chatId", (p) => p.chatId || ""],
  ["chatName", (p) => p.chatName || ""],
  ["organization", (p) => p.orgName || ""],
  ["organizationId", (p) => p.orgId || ""],
  ["uid", (p) => p.uid || ""],
  ["name", (p) => p.name || ""],
  ["email", (p) => p.email || ""],
  ["loanId", (p) => p.loanId || ""],
  ["loanNumber", (p) => p.loanNumber || ""],
  ["loanType", (p) => p.loanType || ""],
  ["loanStage", (p) => p.loanStage || ""],
  ["loanAmount", (p) => p.loanAmount || ""],
  ["borrowerName", (p) => p.borrowerName || ""],
  ["propertyAddress", (p) => p.propertyAddress || ""],
  ["los", (p) => p.los || ""],
  ["loanUrl", (p) => p.loanUrl || ""],
  ["question", (p) => p.question || ""],
  ["answer", (p) => p.answer || ""],
];

function downloadQaCsv(filename, pairs) {
  const esc = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
  const headers = QA_CSV_COLUMNS.map(([h]) => h);
  const body = pairs
    .map((p) => QA_CSV_COLUMNS.map(([, get]) => esc(get(p))).join(","))
    .join("\n");
  const csv = headers.join(",") + "\n" + body;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function downloadJson(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const S = {
  page: {
    padding: "28px 32px 48px",
    minHeight: "100vh",
    background: "#f8f9fa",
    fontFamily: "inherit",
    width: "100%",
  },
  header: { marginBottom: 24 },
  h1: {
    fontSize: "1.5rem",
    fontWeight: 800,
    margin: "0 0 4px",
    display: "flex",
    alignItems: "center",
    gap: 10,
    color: "#111827",
  },
  h1Icon: {
    width: 34,
    height: 34,
    borderRadius: 9,
    background: "linear-gradient(135deg,#7f7fd5,#a78bfa)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.95rem",
    color: "#fff",
    flexShrink: 0,
  },
  subtitle: { color: "#6b7280", fontSize: "0.85rem", margin: 0 },
  controls: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 14,
    marginBottom: 22,
    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
  },
  fieldsRow: {
    display: "grid",
    gridTemplateColumns:
      "minmax(190px, 1fr) minmax(190px, 1.3fr) minmax(220px, 1.4fr) minmax(220px, 1.4fr)",
    gap: 12,
    width: "100%",
  },
  fieldCol: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
    minWidth: 0,
  },
  fieldLabel: {
    fontSize: "0.68rem",
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontWeight: 700,
  },
  fieldInput: {
    width: "100%",
    padding: "9px 12px",
    borderRadius: 8,
    border: "1px solid #d1d5db",
    background: "#fff",
    color: "#111827",
    fontSize: "0.875rem",
    outline: "none",
    boxSizing: "border-box",
  },
  fieldHint: {
    fontSize: "0.75rem",
    color: "#9ca3af",
    marginTop: 10,
  },
  actionRow: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
    marginTop: 12,
  },
  btnPrimary: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "9px 16px",
    borderRadius: 8,
    border: "none",
    background: "#7f7fd5",
    color: "#fff",
    fontSize: "0.85rem",
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  btnGhost: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "9px 16px",
    borderRadius: 8,
    border: "1px solid #d1d5db",
    background: "#fff",
    color: "#374151",
    fontSize: "0.85rem",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  toggleWrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "9px 14px",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
    background: "#f9fafb",
    color: "#374151",
    fontSize: "0.82rem",
    fontWeight: 600,
    cursor: "pointer",
    userSelect: "none",
  },
  toggleInput: {
    margin: 0,
    cursor: "pointer",
    accentColor: "#7f7fd5",
    width: 14,
    height: 14,
  },
  toggleCount: { marginLeft: 6, color: "#9ca3af", fontWeight: 500 },
  pickerCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "12px 14px 8px",
    marginBottom: 22,
    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
  },
  pickerLabel: {
    fontSize: "0.7rem",
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    fontWeight: 700,
    marginBottom: 8,
    padding: "0 6px",
  },
  pickerRow: {
    padding: "10px 12px",
    borderRadius: 8,
    cursor: "pointer",
    transition: "background 0.1s",
  },
  pickerRowName: {
    fontSize: "0.9rem",
    fontWeight: 600,
    color: "#111827",
    marginBottom: 2,
  },
  pickerRowMeta: {
    fontSize: "0.72rem",
    color: "#9ca3af",
    fontFamily: "monospace",
  },
  statsRow: {
    display: "flex",
    gap: 14,
    marginBottom: 22,
    flexWrap: "wrap",
  },
  statCard: (color) => ({
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "14px 20px",
    minWidth: 140,
    flex: "1 1 140px",
    borderTop: `3px solid ${color}`,
    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
  }),
  statLabel: {
    fontSize: "0.7rem",
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    marginBottom: 4,
    fontWeight: 600,
  },
  statValue: (color) => ({
    fontSize: "1.65rem",
    fontWeight: 800,
    color,
    lineHeight: 1,
  }),
  statSub: {
    fontSize: "0.7rem",
    color: "#9ca3af",
    marginTop: 4,
    fontWeight: 500,
  },
  tableCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
  },
  tableHeader: {
    padding: "12px 18px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    background: "#fff",
  },
  tableTitle: { fontWeight: 700, color: "#111827", fontSize: "0.95rem" },
  filterChips: { display: "flex", gap: 6 },
  chip: (active) => ({
    padding: "6px 14px",
    borderRadius: 999,
    border: active ? "1px solid #7f7fd5" : "1px solid #e5e7eb",
    background: active ? "#7f7fd5" : "#fff",
    color: active ? "#fff" : "#374151",
    fontSize: "0.75rem",
    fontWeight: 600,
    cursor: "pointer",
    textTransform: "capitalize",
  }),
  table: { width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" },
  thead: { background: "#f9fafb", borderBottom: "2px solid #e5e7eb" },
  th: {
    padding: "11px 14px",
    textAlign: "left",
    fontWeight: 700,
    color: "#374151",
    fontSize: "0.72rem",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    whiteSpace: "nowrap",
  },
  thInner: { display: "inline-flex", alignItems: "center", gap: 5 },
  infoIconWrap: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    cursor: "help",
  },
  infoIcon: { color: "#9ca3af", fontSize: "0.78rem", transition: "color 0.15s" },
  infoBubble: {
    position: "fixed",
    transform: "translateX(-50%)",
    background: "#111827",
    color: "#f9fafb",
    padding: "8px 12px",
    borderRadius: 6,
    fontSize: "0.72rem",
    fontWeight: 500,
    letterSpacing: 0,
    textTransform: "none",
    lineHeight: 1.4,
    width: 240,
    whiteSpace: "normal",
    textAlign: "left",
    boxShadow: "0 4px 12px rgba(0,0,0,0.18)",
    pointerEvents: "none",
    zIndex: 10000,
  },
  tr: { borderBottom: "1px solid #f3f4f6" },
  trClickable: {
    borderBottom: "1px solid #f3f4f6",
    cursor: "pointer",
  },
  td: {
    padding: "12px 14px",
    verticalAlign: "middle",
    color: "#374151",
  },
  tdEmail: { fontWeight: 600, color: "#111827", whiteSpace: "nowrap" },
  tdMuted: { color: "#6b7280", whiteSpace: "nowrap", fontSize: "0.8rem" },
  tdNum: {
    fontVariantNumeric: "tabular-nums",
    fontWeight: 600,
    color: "#111827",
  },
  empty: { textAlign: "center", padding: "60px 24px", color: "#9ca3af" },
  // Drill-down
  drillPanel: {
    padding: "14px 18px 18px",
    background: "#f9fafb",
    borderTop: "1px dashed #e5e7eb",
    borderBottom: "1px dashed #e5e7eb",
  },
  drillTitle: {
    fontSize: "0.72rem",
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    fontWeight: 700,
    marginBottom: 8,
  },
  chatListRow: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: "10px 14px",
    marginBottom: 8,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  chatHeadName: { fontWeight: 600, color: "#111827", fontSize: "0.88rem" },
  chatHeadMeta: {
    fontSize: "0.72rem",
    color: "#9ca3af",
    fontFamily: "monospace",
  },
  chatHeadStats: {
    marginLeft: "auto",
    display: "flex",
    gap: 12,
    fontSize: "0.75rem",
    color: "#6b7280",
    fontWeight: 600,
  },
  messagesBox: {
    marginTop: 10,
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: 12,
    maxHeight: 520,
    overflowY: "auto",
  },
  msgRow: { marginBottom: 10 },
  msgMeta: {
    fontSize: "0.68rem",
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontWeight: 700,
    marginBottom: 4,
  },
  msgBubbleUser: {
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    borderRadius: 10,
    padding: "10px 12px",
    color: "#1e3a8a",
    fontSize: "0.85rem",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  msgBubbleAssistant: {
    background: "#f5f3ff",
    border: "1px solid #ddd6fe",
    borderRadius: 10,
    padding: "10px 12px",
    color: "#4c1d95",
    fontSize: "0.85rem",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  msgBubbleOther: {
    background: "#f3f4f6",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "10px 12px",
    color: "#374151",
    fontSize: "0.85rem",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  // Recent Q&A
  qaCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
    marginTop: 22,
  },
  qaBody: { padding: "14px 18px 18px" },
  qaSubtitle: { fontSize: "0.75rem", color: "#9ca3af", marginTop: 3 },
  qaItem: {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "12px 14px",
    marginBottom: 10,
    background: "#fcfcfd",
  },
  qaItemHead: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 8,
    fontSize: "0.72rem",
    color: "#6b7280",
    fontWeight: 600,
  },
  qaIndex: {
    fontFamily: "monospace",
    color: "#9ca3af",
    fontWeight: 700,
  },
  qaLoanMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    margin: "0 0 10px",
  },
  qaLoanChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 8px",
    borderRadius: 999,
    background: "#f3f4f6",
    border: "1px solid #e5e7eb",
    color: "#4b5563",
    fontSize: "0.68rem",
    fontWeight: 600,
    maxWidth: "100%",
  },
  qaLoanChipButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 8px",
    borderRadius: 999,
    background: "#f3f4f6",
    border: "1px solid #e5e7eb",
    color: "#4b5563",
    fontSize: "0.68rem",
    fontWeight: 600,
    maxWidth: "100%",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  qaLoanChipLabel: {
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    fontSize: "0.62rem",
    fontWeight: 700,
  },
  qaLoanChipValue: (mono) => ({
    fontFamily: mono ? "monospace" : "inherit",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),
  qaLoanLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "2px 10px",
    borderRadius: 999,
    background: "rgba(127,127,213,0.12)",
    border: "1px solid rgba(127,127,213,0.35)",
    color: "#7F7FD5",
    fontSize: "0.68rem",
    fontWeight: 700,
    textDecoration: "none",
    whiteSpace: "nowrap",
  },
  qaText: {
    fontSize: "0.85rem",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    maxHeight: 180,
    overflowY: "auto",
  },
  qaFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    padding: "6px 0 2px",
    flexWrap: "wrap",
  },
  qaFooterNote: { fontSize: "0.78rem", color: "#9ca3af", fontWeight: 500 },
};

// ─── Small display components ───────────────────────────────────────────────

const PILL_STYLES = {
  yes: { bg: "#ecfdf5", fg: "#047857", border: "#a7f3d0" },
  no: { bg: "#fef2f2", fg: "#b91c1c", border: "#fecaca" },
  pending: { bg: "#fffbeb", fg: "#92400e", border: "#fde68a" },
  accepted: { bg: "#eff6ff", fg: "#1d4ed8", border: "#bfdbfe" },
  neutral: { bg: "#f3f4f6", fg: "#6b7280", border: "#e5e7eb" },
};

function Pill({ tone, children }) {
  const s = PILL_STYLES[tone] || PILL_STYLES.neutral;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 10px",
        borderRadius: 20,
        fontSize: "0.72rem",
        fontWeight: 700,
        letterSpacing: "0.03em",
        background: s.bg,
        color: s.fg,
        border: `1px solid ${s.border}`,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: "currentColor",
          opacity: 0.7,
        }}
      />
      {children}
    </span>
  );
}

function StatusPill({ status }) {
  if (status === "accepted") return <Pill tone="accepted">accepted</Pill>;
  if (status === "pending") return <Pill tone="pending">pending</Pill>;
  return <Pill tone="neutral">{status || "—"}</Pill>;
}

function InfoTip({ text }) {
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const show = () => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ left: r.left + r.width / 2, top: r.bottom + 8 });
  };
  const hide = () => setPos(null);
  return (
    <span
      ref={ref}
      onMouseEnter={show}
      onMouseLeave={hide}
      style={S.infoIconWrap}
    >
      <FaInfoCircle
        style={{ ...S.infoIcon, color: pos ? "#7f7fd5" : S.infoIcon.color }}
      />
      {pos
        ? ReactDOM.createPortal(
            <div style={{ ...S.infoBubble, left: pos.left, top: pos.top }}>
              {text}
            </div>,
            document.body
          )
        : null}
    </span>
  );
}

/**
 * Users who sit in many orgs (support accounts) have no single org to name, so we
 * say so instead of picking one at random.
 */
const orgLabel = (pair) => {
  if (pair.orgName || pair.orgId) return pair.orgName || pair.orgId;
  if (pair.orgIds?.length) return `${pair.orgIds.length} orgs · unresolved`;

  return "no org found";
};

const QA_LOAN_CHIP_FIELDS = [
  { key: "loanNumber", label: "Loan #", mono: true },
  { key: "loanType", label: "Type" },
  { key: "loanStage", label: "Stage" },
  { key: "loanAmount", label: "Amount" },
  { key: "borrowerName", label: "Borrower" },
  { key: "propertyAddress", label: "Property" },
  { key: "los", label: "LOS" },
  { key: "loanId", label: "Loan ID", mono: true },
];

async function copyText(value, label) {
  if (!value) return;
  try {
    await navigator.clipboard.writeText(String(value));
    toast.success(`${label} copied`);
  } catch (err) {
    console.warn("[AskAddyChats] clipboard write failed", err);
    toast.error(`Could not copy ${label.toLowerCase()}`);
  }
}

function QaLoanChips({ pair }) {
  const chips = QA_LOAN_CHIP_FIELDS.filter((f) => pair[f.key]);
  if (!pair.chatId && !pair.loanId && !chips.length) return null;

  return (
    <div style={S.qaLoanMeta}>
      {pair.chatId ? (
        <button
          type="button"
          onClick={() => copyText(pair.chatId, "Chat ID")}
          style={S.qaLoanChipButton}
          title={`Copy chat ID for Firestore: ${pair.chatId}`}
        >
          <span style={S.qaLoanChipLabel}>Chat ID</span>
          <span style={S.qaLoanChipValue(true)}>{pair.chatId}</span>
          <FaCopy style={{ opacity: 0.55, flexShrink: 0 }} />
        </button>
      ) : null}
      {pair.loanUrl ? (
        <a
          href={pair.loanUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={S.qaLoanLink}
        >
          <FaExternalLinkAlt /> Open loan
        </a>
      ) : null}
      {chips.map((chip) => (
        <span key={chip.key} style={S.qaLoanChip} title={pair[chip.key]}>
          <span style={S.qaLoanChipLabel}>{chip.label}</span>
          <span style={S.qaLoanChipValue(chip.mono)}>{pair[chip.key]}</span>
        </span>
      ))}
    </div>
  );
}

const COLUMNS = [
  { label: "", tip: "" }, // expand chevron
  { label: "Email", tip: "Org member email." },
  { label: "Role", tip: "Role in this org." },
  {
    label: "Status",
    tip: 'Invite status — "accepted" = joined this org, "pending" = invite not yet accepted.',
  },
  {
    label: "Chats",
    tip: "Number of sidebar chat sessions the user has with Ask Addy (one row per chat in sidebar-chat-history).",
  },
  {
    label: "Messages",
    tip: "Total messages across all of this user's chats (sum of messages[] lengths).",
  },
  {
    label: "First chat",
    tip: "Earliest dateStarted across this user's chats.",
  },
  {
    label: "Last active",
    tip: "Most recent lastActive timestamp across this user's chats.",
  },
  {
    label: "Loans",
    tip: "Distinct loanId values referenced by this user's chats (sidebar chat loanId field).",
  },
];

// ─── Page ────────────────────────────────────────────────────────────────────

const ADDY_INTERNAL_DOMAINS = ["addy-ai.com", "addy.com", "heyaddy.com"];

const isAddyTeam = (email) => {
  if (!email) return false;
  const lower = email.toLowerCase();
  return ADDY_INTERNAL_DOMAINS.some((d) => lower.endsWith("@" + d));
};

const normalizeMessage = (m, idx) => {
  if (!m || typeof m !== "object") {
    return { role: "other", content: String(m || ""), ts: 0, idx };
  }
  const role =
    m.role ||
    m.author ||
    m.sender ||
    (m.type === "user" || m.isUser ? "user" : "") ||
    (m.type === "assistant" || m.type === "ai" ? "assistant" : "") ||
    "other";
  const content =
    typeof m.content === "string"
      ? m.content
      : typeof m.text === "string"
      ? m.text
      : typeof m.message === "string"
      ? m.message
      : typeof m.query === "string"
      ? m.query
      : typeof m.response === "string"
      ? m.response
      : JSON.stringify(m);
  const ts = tsToMs(m.timestamp || m.createdAt || m.time || m.date);
  return { role, content, ts, idx };
};

const isAssistantRole = (role) =>
  role === "assistant" || role === "ai" || role === "bot" || role === "addy";

const QA_PAGE_SIZE = 100;

// How many chat docs to pull per Firestore round-trip while filling a page of
// exchanges. Chats hold many messages each, so a small batch usually suffices.
const CHAT_FETCH_BATCH = 40;

// Safety valve so a run of empty chats can't spin the loop forever.
const MAX_BATCHES_PER_PAGE = 25;

/**
 * Turns one chat's messages into question/answer pairs (a user message plus the
 * assistant reply that follows it), newest first. Messages without a timestamp
 * fall back to the chat's last activity so ordering stays stable.
 */
function buildChatQaPairs(base, messages, fallbackTs) {
  const pairs = [];
  let pending = null;

  const flush = () => {
    if (!pending) return;
    pairs.push(pending);
    pending = null;
  };

  messages.forEach((m, idx) => {
    const n = normalizeMessage(m, idx);
    if (!n.content) return;

    if (isAssistantRole(n.role)) {
      if (pending && !pending.answer) {
        pending.answer = n.content;
        flush();

        return;
      }
      flush();
      pairs.push({
        ...base,
        key: `${base.chatId}:${idx}`,
        ts: n.ts || fallbackTs,
        order: idx,
        question: "",
        answer: n.content,
      });

      return;
    }

    if (n.role !== "user") return;

    flush();
    pending = {
      ...base,
      key: `${base.chatId}:${idx}`,
      ts: n.ts || fallbackTs,
      order: idx,
      question: n.content,
      answer: "",
    };
  });

  flush();

  return sortPairsNewestFirst(pairs);
}

function sortPairsNewestFirst(pairs) {
  return pairs.sort((a, b) => {
    if (b.ts !== a.ts) return b.ts - a.ts;

    return b.order - a.order;
  });
}

const AskAddyChatsPage = ({ onReady }) => {
  const [nameInput, setNameInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [orgIdInput, setOrgIdInput] = useState("");
  const [env, setEnv] = useState("prod");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(null);
  const [totalChatCount, setTotalChatCount] = useState(0);
  const [filterStatus, setFilterStatus] = useState("active");
  const [excludeAddy, setExcludeAddy] = useState(true);
  const [searchedOrgId, setSearchedOrgId] = useState("");
  const [searchedOrgName, setSearchedOrgName] = useState("");
  const [pickerCandidates, setPickerCandidates] = useState(null);
  const [openUid, setOpenUid] = useState(null);
  const [openChatId, setOpenChatId] = useState(null);

  const [qaPairs, setQaPairs] = useState([]);
  const [qaLoading, setQaLoading] = useState(false);
  const [qaExhausted, setQaExhausted] = useState(false);
  const [qaChatsScanned, setQaChatsScanned] = useState(0);
  const [qaExcludeAddy, setQaExcludeAddy] = useState(true);
  const qaCursorRef = useRef(null);
  const authorCacheRef = useRef(new Map());
  const loanCacheRef = useRef(createLoanCache());
  const orgNameCacheRef = useRef(new Map());

  const [orgCache, setOrgCache] = useState({ prod: null, dev: null });
  const [orgCacheLoading, setOrgCacheLoading] = useState(false);

  useEffect(() => {
    onReady && onReady();
  }, [onReady]);

  const ensureOrgCache = useCallback(async () => {
    if (orgCache[env]) return orgCache[env];
    setOrgCacheLoading(true);
    const loadingToastId = toast.info("Loading orgs…", { autoClose: false });
    try {
      const db = env === "dev" ? firestoreDev : firestoreProd;
      const orgs = await loadAllOrgs(db);
      setOrgCache((c) => ({ ...c, [env]: orgs }));
      toast.update(loadingToastId, {
        render: `Loaded ${orgs.length} orgs`,
        type: "success",
        autoClose: 2000,
      });
      return orgs;
    } catch (err) {
      console.error(err);
      toast.update(loadingToastId, {
        render: "Failed to load organizations list",
        type: "error",
        autoClose: 3000,
      });
      return [];
    } finally {
      setOrgCacheLoading(false);
    }
  }, [env, orgCache]);

  const runForOrg = useCallback(
    async (orgId, displayName) => {
      const trimmed = orgId.trim();
      if (!trimmed) return;
      setLoading(true);
      setRows(null);
      setPickerCandidates(null);
      setOpenUid(null);
      setOpenChatId(null);
      const pid = toast.info("Loading members…", { autoClose: false });
      try {
        const db = env === "dev" ? firestoreDev : firestoreProd;
        toast.update(pid, { render: "Loading members…" });
        const { rows, chatCount } = await runReport(db, trimmed);
        setRows(rows);
        setTotalChatCount(chatCount);
        setSearchedOrgId(trimmed);
        setSearchedOrgName(displayName || "");
        toast.update(pid, {
          render: rows.length
            ? `Found ${chatCount} chats across ${
                rows.filter((r) => r.chatCount > 0).length
              } users`
            : "No members found for this org.",
          type: rows.length ? "success" : "info",
          autoClose: 2500,
        });
      } catch (err) {
        console.error(err);
        toast.update(pid, {
          render: err.message || "Failed to load chats",
          type: "error",
          autoClose: 3500,
        });
      } finally {
        setLoading(false);
      }
    },
    [env]
  );

  const handleRun = useCallback(async () => {
    const id = orgIdInput.trim();
    const email = emailInput.trim();
    const name = nameInput.trim();

    setPickerCandidates(null);

    if (id) {
      const cached = (orgCache[env] || []).find((o) => o.id === id);
      runForOrg(id, cached ? orgDisplayName(cached) : "");
      return;
    }

    if (email) {
      setLoading(true);
      try {
        const db = env === "dev" ? firestoreDev : firestoreProd;
        const ids = await findOrgIdsByEmail(db, email);
        if (ids.length === 0) {
          toast.error(`No orgs found with member email "${email}".`);
          return;
        }
        const cache = await ensureOrgCache();
        const candidates = ids.map((oid) => {
          const o = (cache || []).find((x) => x.id === oid);
          return o || { id: oid, name: "(org not in cache)" };
        });
        if (candidates.length === 1) {
          runForOrg(candidates[0].id, orgDisplayName(candidates[0]));
        } else {
          setPickerCandidates(candidates);
          toast.info(`${candidates.length} orgs match — pick one.`);
        }
      } catch (err) {
        console.error(err);
        toast.error(err.message || "Email lookup failed");
      } finally {
        setLoading(false);
      }
      return;
    }

    if (name) {
      setLoading(true);
      try {
        const cache = await ensureOrgCache();
        const matches = fuzzyMatchOrgs(cache || [], name);
        if (matches.length === 0) {
          toast.error(`No orgs matching "${name}".`);
          return;
        }
        if (matches.length === 1) {
          runForOrg(matches[0].id, orgDisplayName(matches[0]));
        } else {
          setPickerCandidates(matches);
          toast.info(`${matches.length} orgs match — pick one.`);
        }
      } finally {
        setLoading(false);
      }
      return;
    }

    toast.error("Enter an org ID, email, or name.");
  }, [
    orgIdInput,
    emailInput,
    nameInput,
    env,
    orgCache,
    ensureOrgCache,
    runForOrg,
  ]);

  const handleFieldKeyDown = (e) => {
    if (e.key === "Enter") handleRun();
  };

  const scopedRows = useMemo(() => {
    if (!rows) return null;
    return excludeAddy ? rows.filter((r) => !isAddyTeam(r.email)) : rows;
  }, [rows, excludeAddy]);

  const addyTeamCount = useMemo(
    () => (rows ? rows.filter((r) => isAddyTeam(r.email)).length : 0),
    [rows]
  );

  const stats = useMemo(() => {
    if (!scopedRows) return null;
    const totalUsers = scopedRows.length;
    const activeUsers = scopedRows.filter((r) => r.chatCount > 0).length;
    const totalChats = scopedRows.reduce((a, r) => a + r.chatCount, 0);
    const totalMessages = scopedRows.reduce((a, r) => a + r.messageCount, 0);
    const mostRecent = scopedRows.reduce(
      (a, r) => Math.max(a, r.lastActiveAt || 0),
      0
    );
    return { totalUsers, activeUsers, totalChats, totalMessages, mostRecent };
  }, [scopedRows]);

  const resetQaFeed = useCallback(() => {
    qaCursorRef.current = null;
    setQaPairs([]);
    setQaExhausted(false);
    setQaChatsScanned(0);
  }, []);

  // Switching environment invalidates the cursor and every resolved cache.
  useEffect(() => {
    authorCacheRef.current = new Map();
    loanCacheRef.current = createLoanCache();
    orgNameCacheRef.current = new Map();
    resetQaFeed();
  }, [env, resetQaFeed]);

  const loadMoreQa = useCallback(async () => {
    setQaLoading(true);
    const db = env === "dev" ? firestoreDev : firestoreProd;
    const collected = [];
    let cursor = qaCursorRef.current;
    let scanned = 0;
    let exhausted = false;

    try {
      for (
        let batch = 0;
        batch < MAX_BATCHES_PER_PAGE && collected.length < QA_PAGE_SIZE;
        batch++
      ) {
        const constraints = [
          orderBy("lastActive", "desc"),
          ...(cursor ? [startAfter(cursor)] : []),
          limit(CHAT_FETCH_BATCH),
        ];
        const snap = await getDocs(
          query(collection(db, "sidebar-chat-history"), ...constraints)
        );

        if (snap.empty) {
          exhausted = true;
          break;
        }

        cursor = snap.docs[snap.docs.length - 1];
        scanned += snap.docs.length;

        for (const d of snap.docs) {
          const data = d.data();
          const messages = Array.isArray(data.messages) ? data.messages : [];
          if (messages.length === 0) continue;
          const chatId = data.chatId || d.id;
          collected.push(
            ...buildChatQaPairs(
              {
                uid: data.uid || "",
                chatId,
                chatName: data.chatName || "Chat",
                loanId: data.loanId || null,
              },
              messages,
              tsToMs(data.lastActive) || tsToMs(data.dateStarted)
            )
          );
        }

        if (snap.docs.length < CHAT_FETCH_BATCH) {
          exhausted = true;
          break;
        }
      }

      const authors = await resolveAuthors(
        db,
        collected.map((p) => p.uid),
        authorCacheRef.current,
        orgNameCacheRef.current
      );
      const withAuthors = collected.map((p) => ({
        ...p,
        ...(authors.get(p.uid) || {}),
        loanUrl: loanAppUrl(env, p.loanId),
      }));
      const loansByLoanId = await resolveLoansForPairs(
        db,
        withAuthors,
        loanCacheRef.current
      );
      const enriched = sortPairsNewestFirst(
        withAuthors.map((p) =>
          attachLoanMeta(p, loansByLoanId, orgNameCacheRef.current)
        )
      );

      qaCursorRef.current = cursor;
      setQaChatsScanned((n) => n + scanned);
      setQaExhausted(exhausted);
      setQaPairs((cur) => [...cur, ...enriched]);

      if (enriched.length === 0 && exhausted) {
        toast.info("No more chats to read.");
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to load recent messages");
    } finally {
      setQaLoading(false);
    }
  }, [env]);

  const visibleQaPairs = useMemo(
    () =>
      qaExcludeAddy ? qaPairs.filter((p) => !isAddyTeam(p.email)) : qaPairs,
    [qaPairs, qaExcludeAddy]
  );

  const filtered = useMemo(() => {
    if (!scopedRows) return [];
    if (filterStatus === "all") return scopedRows;
    if (filterStatus === "active")
      return scopedRows.filter((r) => r.chatCount > 0);
    if (filterStatus === "silent")
      return scopedRows.filter((r) => r.chatCount === 0);
    return scopedRows;
  }, [scopedRows, filterStatus]);

  const toggleUid = (uid) => {
    setOpenUid((cur) => (cur === uid ? null : uid));
    setOpenChatId(null);
  };
  const toggleChat = (cid) => {
    setOpenChatId((cur) => (cur === cid ? null : cid));
  };

  return (
    <div className="container-fluid AskAddyChatsPageContainer" style={S.page}>
      <ToastContainer position="bottom-right" theme="light" />

      <div style={S.header}>
        <h1 style={S.h1}>
          <span style={S.h1Icon}>
            <FaCommentDots />
          </span>
          Ask Addy Chats
        </h1>
        <p style={S.subtitle}>
          Monitor per-user sidebar chat activity for an organization. Pick an
          org, then click a row to drill down into that user's chats and read
          the conversation.
        </p>
      </div>

      {/* Controls */}
      <div style={S.controls}>
        <div style={S.fieldsRow}>
          <div style={S.fieldCol}>
            <div style={S.fieldLabel}>Environment</div>
            <select
              value={env}
              onChange={(e) => setEnv(e.target.value)}
              style={S.fieldInput}
            >
              <option value="prod">prod (hey-addy-chatgpt)</option>
              <option value="dev">dev (addy-ai-dev)</option>
            </select>
          </div>
          <div style={S.fieldCol}>
            <div style={S.fieldLabel}>Org name (fuzzy)</div>
            <input
              type="text"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={handleFieldKeyDown}
              placeholder="e.g. emc"
              style={S.fieldInput}
            />
          </div>
          <div style={S.fieldCol}>
            <div style={S.fieldLabel}>Owner / member email</div>
            <input
              type="text"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={handleFieldKeyDown}
              placeholder="someone@company.com"
              style={S.fieldInput}
            />
          </div>
          <div style={S.fieldCol}>
            <div style={S.fieldLabel}>Org ID</div>
            <input
              type="text"
              value={orgIdInput}
              onChange={(e) => setOrgIdInput(e.target.value)}
              onKeyDown={handleFieldKeyDown}
              placeholder="aGWer6DsXt9xHRelW2kl"
              style={{ ...S.fieldInput, fontFamily: "monospace" }}
            />
          </div>
        </div>
        <div style={S.fieldHint}>
          Org ID wins over email, which wins over name. Nothing runs until you
          click <b>Run report</b>.
        </div>
        <div style={S.actionRow}>
          <button
            onClick={handleRun}
            disabled={loading}
            style={{
              ...S.btnPrimary,
              opacity: loading ? 0.65 : 1,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? <FaSync className="aac-spin" /> : <FaSearch />}
            {loading
              ? orgCacheLoading
                ? "Loading orgs…"
                : "Loading…"
              : "Run report"}
          </button>
          {rows && rows.length ? (
            <button
              onClick={() =>
                downloadCsv(
                  `ask-addy-chats-${searchedOrgId}${
                    excludeAddy ? "-clients" : "-all"
                  }.csv`,
                  scopedRows || []
                )
              }
              style={S.btnGhost}
            >
              <FaDownload /> CSV
            </button>
          ) : null}
          {rows && rows.length ? (
            <label style={S.toggleWrap} title="Hides @addy-ai.com members">
              <input
                type="checkbox"
                checked={excludeAddy}
                onChange={(e) => setExcludeAddy(e.target.checked)}
                style={S.toggleInput}
              />
              <span>
                Exclude Addy team
                {addyTeamCount > 0 ? (
                  <span style={S.toggleCount}>({addyTeamCount})</span>
                ) : null}
              </span>
            </label>
          ) : null}
        </div>
      </div>

      {/* Picker card */}
      {pickerCandidates && pickerCandidates.length > 1 ? (
        <div style={S.pickerCard}>
          <div style={S.pickerLabel}>
            {pickerCandidates.length} matching orgs — pick one
          </div>
          {pickerCandidates.map((c) => (
            <div
              key={c.id}
              style={S.pickerRow}
              onClick={() => runForOrg(c.id, orgDisplayName(c))}
            >
              <div style={S.pickerRowName}>
                {orgDisplayName(c) || "(unnamed org)"}
              </div>
              <div style={S.pickerRowMeta}>{c.id}</div>
            </div>
          ))}
        </div>
      ) : null}

      {stats ? (
        <>
          <div style={S.statsRow}>
            <div style={S.statCard("#7f7fd5")}>
              <div style={S.statLabel}>Total chats</div>
              <div style={S.statValue("#7f7fd5")}>{stats.totalChats}</div>
              <div style={S.statSub}>
                <FaBuilding style={{ marginRight: 5, opacity: 0.6 }} />
                {searchedOrgName ? `${searchedOrgName} · ` : ""}
                {searchedOrgId}
              </div>
            </div>
            <div style={S.statCard("#10b981")}>
              <div style={S.statLabel}>Active users</div>
              <div style={S.statValue("#10b981")}>{stats.activeUsers}</div>
              <div style={S.statSub}>
                <FaUsers style={{ marginRight: 5, opacity: 0.6 }} />
                of {stats.totalUsers} members
              </div>
            </div>
            <div style={S.statCard("#3b82f6")}>
              <div style={S.statLabel}>Total messages</div>
              <div style={S.statValue("#3b82f6")}>{stats.totalMessages}</div>
              <div style={S.statSub}>
                <FaComments style={{ marginRight: 5, opacity: 0.6 }} />
                across all sessions
              </div>
            </div>
            <div style={S.statCard("#f59e0b")}>
              <div style={S.statLabel}>Last activity</div>
              <div style={S.statValue("#f59e0b")}>
                {stats.mostRecent ? fmtDate(stats.mostRecent) : "—"}
              </div>
              <div style={S.statSub}>
                <FaClock style={{ marginRight: 5, opacity: 0.6 }} />
                most recent chat
              </div>
            </div>
          </div>

          <div style={S.tableCard}>
            <div style={S.tableHeader}>
              <div style={S.tableTitle}>
                Users ({filtered.length}
                {filtered.length !== (scopedRows || []).length
                  ? ` of ${(scopedRows || []).length}`
                  : ""}
                )
              </div>
              <div style={S.filterChips}>
                {["active", "all", "silent"].map((s) => (
                  <button
                    key={s}
                    onClick={() => setFilterStatus(s)}
                    style={S.chip(filterStatus === s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={S.table}>
                <thead style={S.thead}>
                  <tr>
                    {COLUMNS.map((c, i) => (
                      <th key={i} style={S.th}>
                        <span style={S.thInner}>
                          {c.label}
                          {c.tip ? <InfoTip text={c.tip} /> : null}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td
                        colSpan={COLUMNS.length}
                        style={{ ...S.td, ...S.empty }}
                      >
                        No users match this filter.
                      </td>
                    </tr>
                  ) : null}
                  {filtered.map((r) => {
                    const isOpen = openUid === r.uid;
                    return (
                      <React.Fragment key={r.uid}>
                        <tr
                          style={S.trClickable}
                          onClick={() => toggleUid(r.uid)}
                        >
                          <td style={{ ...S.td, width: 32, color: "#9ca3af" }}>
                            {isOpen ? <FaChevronDown /> : <FaChevronRight />}
                          </td>
                          <td style={{ ...S.td, ...S.tdEmail }}>
                            <FaRegEnvelope
                              style={{
                                marginRight: 6,
                                opacity: 0.4,
                                fontSize: "0.75rem",
                              }}
                            />
                            {r.email}
                          </td>
                          <td style={S.td}>{r.role || "—"}</td>
                          <td style={S.td}>
                            <StatusPill status={r.status} />
                          </td>
                          <td style={{ ...S.td, ...S.tdNum }}>{r.chatCount}</td>
                          <td style={{ ...S.td, ...S.tdNum }}>
                            {r.messageCount}
                          </td>
                          <td style={{ ...S.td, ...S.tdMuted }}>
                            {fmtDate(r.firstChatAt)}
                          </td>
                          <td style={{ ...S.td, ...S.tdMuted }}>
                            {fmtDate(r.lastActiveAt)}
                          </td>
                          <td style={{ ...S.td, ...S.tdNum }}>
                            {r.loanIds.length}
                          </td>
                        </tr>
                        {isOpen ? (
                          <tr>
                            <td
                              colSpan={COLUMNS.length}
                              style={{ padding: 0 }}
                            >
                              <div style={S.drillPanel}>
                                <div style={S.drillTitle}>
                                  {r.chatCount} chat
                                  {r.chatCount === 1 ? "" : "s"} for {r.email}
                                </div>
                                {r.chats.length === 0 ? (
                                  <div style={S.tdMuted}>
                                    No chats for this user yet.
                                  </div>
                                ) : (
                                  r.chats.map((c) => {
                                    const isChatOpen = openChatId === c.chatId;
                                    return (
                                      <div key={c.chatId}>
                                        <div
                                          style={S.chatListRow}
                                          onClick={() => toggleChat(c.chatId)}
                                        >
                                          <span style={{ color: "#9ca3af" }}>
                                            {isChatOpen ? (
                                              <FaChevronDown />
                                            ) : (
                                              <FaChevronRight />
                                            )}
                                          </span>
                                          <div>
                                            <div style={S.chatHeadName}>
                                              {c.chatName}
                                            </div>
                                            <div style={S.chatHeadMeta}>
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  copyText(c.chatId, "Chat ID");
                                                }}
                                                style={{
                                                  ...S.qaLoanChipButton,
                                                  padding: "0 6px",
                                                  marginRight: 4,
                                                }}
                                                title={`Copy chat ID for Firestore: ${c.chatId}`}
                                              >
                                                {c.chatId}
                                                <FaCopy
                                                  style={{
                                                    opacity: 0.55,
                                                    flexShrink: 0,
                                                  }}
                                                />
                                              </button>
                                              {c.loanId
                                                ? ` · loan ${c.loanId}`
                                                : ""}
                                              {c.clientHostname
                                                ? ` · ${c.clientHostname}`
                                                : ""}
                                            </div>
                                          </div>
                                          <div style={S.chatHeadStats}>
                                            <span>
                                              {c.messageCount} msg
                                              {c.messageCount === 1 ? "" : "s"}
                                            </span>
                                            <span>
                                              {fmtDateTime(c.lastActive)}
                                            </span>
                                          </div>
                                        </div>
                                        {isChatOpen ? (
                                          <div style={S.messagesBox}>
                                            <div
                                              style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                marginBottom: 10,
                                              }}
                                            >
                                              <div style={S.msgMeta}>
                                                started {fmtDateTime(c.dateStarted)}
                                              </div>
                                              <button
                                                style={{
                                                  ...S.btnGhost,
                                                  padding: "6px 10px",
                                                  fontSize: "0.72rem",
                                                }}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  downloadJson(
                                                    `chat-${c.chatId}.json`,
                                                    c.raw
                                                  );
                                                }}
                                              >
                                                <FaFileCode /> Export JSON
                                              </button>
                                            </div>
                                            {c.messages.length === 0 ? (
                                              <div style={S.tdMuted}>
                                                Chat has no messages stored.
                                              </div>
                                            ) : (
                                              c.messages.map((m, idx) => {
                                                const n = normalizeMessage(
                                                  m,
                                                  idx
                                                );
                                                const bubbleStyle =
                                                  n.role === "user"
                                                    ? S.msgBubbleUser
                                                    : n.role === "assistant" ||
                                                      n.role === "ai"
                                                    ? S.msgBubbleAssistant
                                                    : S.msgBubbleOther;
                                                return (
                                                  <div
                                                    key={idx}
                                                    style={S.msgRow}
                                                  >
                                                    <div style={S.msgMeta}>
                                                      {n.role}
                                                      {n.ts
                                                        ? ` · ${fmtDateTime(
                                                            n.ts
                                                          )}`
                                                        : ""}
                                                    </div>
                                                    <div style={bubbleStyle}>
                                                      {n.content}
                                                    </div>
                                                  </div>
                                                );
                                              })
                                            )}
                                          </div>
                                        ) : null}
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

        </>
      ) : loading ? (
        <div style={S.empty}>Loading chats…</div>
      ) : (
        <div style={S.empty}>
          <div style={{ fontSize: "2rem", marginBottom: 10, opacity: 0.4 }}>
            <FaCommentDots />
          </div>
          Fill in one of the fields above and click <b>Run report</b>.
        </div>
      )}

      {/* Global feed — independent of the org report above */}
      <div style={S.qaCard}>
        <div style={S.tableHeader}>
          <div>
            <div style={S.tableTitle}>
              Latest Q&amp;A across all orgs
              {qaPairs.length ? ` (${visibleQaPairs.length})` : ""}
            </div>
            <div style={S.qaSubtitle}>
              Newest exchanges in <b>{env}</b>, whole database. Loads{" "}
              {QA_PAGE_SIZE} at a time. Org comes from membership, org
              ownership, or the user's invite link; each exchange shows its
              full chat ID (click to copy for Firestore). Loan-scoped chats
              also show loan #, type, stage, amount, borrower, property, and
              a link into the app.
              {qaChatsScanned
                ? ` ${qaChatsScanned} chats read so far.`
                : ""}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <label style={S.toggleWrap} title="Hides @addy-ai.com senders">
              <input
                type="checkbox"
                checked={qaExcludeAddy}
                onChange={(e) => setQaExcludeAddy(e.target.checked)}
                style={S.toggleInput}
              />
              <span>Exclude Addy team</span>
            </label>
            {visibleQaPairs.length ? (
              <button
                onClick={() =>
                  downloadQaCsv(
                    `ask-addy-latest-qa-${env}-${visibleQaPairs.length}.csv`,
                    visibleQaPairs
                  )
                }
                style={S.btnGhost}
              >
                <FaDownload /> Export {visibleQaPairs.length} to CSV
              </button>
            ) : null}
            {qaPairs.length ? (
              <button onClick={resetQaFeed} style={S.btnGhost} disabled={qaLoading}>
                <FaSync /> Reset
              </button>
            ) : null}
          </div>
        </div>
        <div style={S.qaBody}>
          {visibleQaPairs.map((p, i) => (
            <div key={p.key + ":" + i} style={S.qaItem}>
              <div style={S.qaItemHead}>
                <span style={S.qaIndex}>#{i + 1}</span>
                <span>{fmtDateTime(p.ts)}</span>
                <span>· {orgLabel(p)}</span>
                <span>· {p.email || p.name || `uid ${p.uid}`}</span>
                <span style={S.chatHeadMeta}>· {p.chatName}</span>
              </div>
              <QaLoanChips pair={p} />
              <div style={S.msgMeta}>user</div>
              <div style={{ ...S.msgBubbleUser, ...S.qaText }}>
                {p.question || "—"}
              </div>
              <div style={{ ...S.msgMeta, marginTop: 8 }}>answer</div>
              <div style={{ ...S.msgBubbleAssistant, ...S.qaText }}>
                {p.answer || "—"}
              </div>
            </div>
          ))}
          {qaPairs.length && !visibleQaPairs.length ? (
            <div style={S.empty}>
              All {qaPairs.length} loaded exchanges are from the Addy team.
            </div>
          ) : null}
          <div style={S.qaFooter}>
            {qaExhausted ? (
              <span style={S.qaFooterNote}>
                Reached the end of the collection — {qaPairs.length} exchanges
                loaded.
              </span>
            ) : (
              <button
                onClick={loadMoreQa}
                disabled={qaLoading}
                style={{
                  ...S.btnPrimary,
                  opacity: qaLoading ? 0.65 : 1,
                  cursor: qaLoading ? "not-allowed" : "pointer",
                }}
              >
                {qaLoading ? (
                  <FaSync className="aac-spin" />
                ) : (
                  <FaChevronDown />
                )}
                {qaLoading
                  ? "Loading…"
                  : qaPairs.length
                  ? `Load ${QA_PAGE_SIZE} more`
                  : `Load latest ${QA_PAGE_SIZE}`}
              </button>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .aac-spin { animation: aac-spin 1s linear infinite; }
        @keyframes aac-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AskAddyChatsPage;
